import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plug } from 'lucide-react'
import Modal from '../ui/Modal'
import { api, apiErrorMessage } from '../../lib/api'
import type { EmailAccountDto } from '@toga/shared'
import { EMAIL_PRESETS, presetForHost, type EmailPreset } from './presets'

/**
 * Reconectar una cuenta de correo: nueva contraseña, o nuevo proveedor.
 *
 * Nació para la cuenta que cae en ERROR_AUTH (clave de cifrado rotada, app
 * password caducada). Pero el caso real que faltaba es el CAMBIO DE PROVEEDOR:
 * el correo de la empresa se movió de Hostinger a Google Workspace y la cuenta
 * seguía «activa» leyendo un buzón al que ya no llega nada. Por eso ahora se
 * puede elegir el proveedor —que rellena IMAP y SMTP a la vez— y se ofrece
 * siempre, no solo cuando la cuenta está en error.
 *
 * El servidor verifica contra IMAP antes de guardar: si la contraseña no vale,
 * la cuenta se queda como estaba. Los correos ya descargados se conservan.
 */
export default function ReconnectAccountModal({
  account,
  onClose,
  onReconnected,
}: {
  account: EmailAccountDto | null
  onClose: () => void
  onReconnected: () => void
}) {
  const { t } = useTranslation(['email', 'common'])
  const [preset, setPreset] = useState<EmailPreset>(EMAIL_PRESETS[EMAIL_PRESETS.length - 1]!)
  const [password, setPassword] = useState('')
  const [imapHost, setImapHost] = useState('')
  const [imapPort, setImapPort] = useState('993')
  const [smtpHost, setSmtpHost] = useState('')
  const [smtpPort, setSmtpPort] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!account) return
    setPassword('')
    setError(null)
    const p = presetForHost(account.imapHost)
    setPreset(p)
    setImapHost(account.imapHost ?? '')
    setImapPort(String(account.imapPort ?? 993))
    setSmtpHost(account.smtpHost ?? '')
    setSmtpPort(account.smtpPort ? String(account.smtpPort) : '')
  }, [account])

  const pickPreset = (p: EmailPreset) => {
    setPreset(p)
    if (p.key === 'custom') return
    setImapHost(p.imapHost)
    setImapPort(String(p.imapPort))
    setSmtpHost(p.smtpHost)
    setSmtpPort(String(p.smtpPort))
  }

  const submit = async () => {
    if (!account || !password) return
    setBusy(true)
    setError(null)
    try {
      await api.post(`/email-accounts/${account.id}/reconnect`, {
        password,
        imapHost: imapHost.trim() || undefined,
        imapPort: imapPort ? Number(imapPort) : undefined,
        imapUseTls: preset.key === 'custom' ? undefined : preset.imapUseTls,
        // El SMTP viaja explícito: si se deja vacío se BORRA, que es lo que
        // quiere decir dejarlo vacío. Sin esto, cambiar de proveedor dejaba
        // el envío apuntando al servidor antiguo.
        smtpHost: smtpHost.trim() || null,
        smtpPort: smtpHost.trim() && smtpPort ? Number(smtpPort) : null,
        smtpUseTls: smtpHost.trim() ? (preset.key === 'custom' ? null : preset.smtpUseTls) : null,
      })
      setPassword('')
      onReconnected()
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err, t('common:errors.generic')))
    } finally {
      setBusy(false)
    }
  }

  const inputCls =
    'w-full px-3 py-2 rounded-lg text-sm surface-soft border border-base t-primary'

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      title={t('reconnect.title')}
      description={account?.emailAddress}
      maxWidth="max-w-md"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        className="space-y-3"
      >
        <p className="text-sm t-secondary">{t('reconnect.intro')}</p>

        <div>
          <label className="block text-xs t-muted mb-1">{t('reconnect.provider')}</label>
          <div className="grid grid-cols-4 gap-1.5">
            {EMAIL_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => pickPreset(p)}
                className={`px-2 py-1.5 rounded-lg text-xs border ${
                  preset.key === p.key
                    ? 'border-toga-500/60 bg-toga-500/10 t-primary font-medium'
                    : 'border-base t-secondary hover:bg-hover'
                }`}
              >
                {t(p.labelKey)}
              </button>
            ))}
          </div>
          {preset.hintKey && <p className="text-xs t-muted mt-1">{t(preset.hintKey)}</p>}
        </div>

        <div>
          <label className="block text-xs t-muted mb-1">{t('reconnect.password')}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="off"
            autoFocus
            className={inputCls}
          />
          <p className="text-xs t-muted mt-1">{t('reconnect.passwordHint')}</p>
        </div>

        <div className="grid grid-cols-[1fr_110px] gap-2">
          <div>
            <label className="block text-xs t-muted mb-1">{t('reconnect.imapHost')}</label>
            <input
              type="text"
              value={imapHost}
              onChange={(e) => setImapHost(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('reconnect.imapPort')}</label>
            <input
              type="number"
              value={imapPort}
              onChange={(e) => setImapPort(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-[1fr_110px] gap-2">
          <div>
            <label className="block text-xs t-muted mb-1">{t('reconnect.smtpHost')}</label>
            <input
              type="text"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder={t('reconnect.smtpPlaceholder')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-xs t-muted mb-1">{t('reconnect.smtpPort')}</label>
            <input
              type="number"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>
        <p className="text-xs t-muted">{t('reconnect.smtpHint')}</p>
        {account && imapHost.trim().toLowerCase() !== (account.imapHost ?? '').trim().toLowerCase() && (
          <p className="text-xs rounded-lg p-2 text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/30">
            {t('reconnect.mailboxChangeHint', { days: 90 })}
          </p>
        )}

        {error && (
          <p className="text-xs rounded-lg p-2 text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !password}
            className="px-3.5 py-1.5 rounded-lg text-sm font-semibold text-white bg-toga-600 hover:bg-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plug size={14} />}
            {t('reconnect.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
